import { ErrorBoundary } from '@sleepstation/components'
import GlobalStyles from '@sleepstation/styles-global'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createBrowserRouter } from 'react-router-dom'
import { css } from 'styled-components'
import { isDevelopmentBuild, isTestBuild } from './build/utils/environment'
import CONTENT from './content'
import AppModule from './view/components/AppModule'
import RootLayoutModule from './view/components/AppModule/App/RootLayoutModule'
import Providers from './view/components/Providers'
import TopLevelErrorNotification from './view/components/errorNotifications/TopLevelErrorNotification'
import ErrorPageModule from './view/components/pages/ErrorPageModule'
import FeedbackEndOfTherapyModule from './view/components/pages/FeedbackEndOfTherapyModule'
import FeedbackEndOfTherapyThankYouModule from './view/components/pages/FeedbackEndOfTherapyThankYouModule'
import FeedbackThankYouModule from './view/components/pages/FeedbackThankYouModule'
import FeedbackVideoTestimonialModule from './view/components/pages/FeedbackVideoTestimonialModule'
import FeedbackWrittenTestimonialModule from './view/components/pages/FeedbackWrittenTestimonialModule'
import HubAboutTherapyModule from './view/components/pages/HubAboutTherapyModule'
import HubAccessTherapyModule from './view/components/pages/HubAccessTherapyModule'
import HubAboutNhsTherapyModule from './view/components/pages/HubAboutNhsTherapyModule'
import HubBuyTherapyModule from './view/components/pages/HubBuyTherapyModule'
import HubBuyTherapyGrantModule from './view/components/pages/HubBuyTherapyGrantModule'
import HubDbasQuizIntroModule from './view/components/pages/HubDbasQuizIntroModule'
import HubDbasQuizResultsModule from './view/components/pages/HubDbasQuizResultsModule'
import HubDbasQuestionnaireModule from './view/components/pages/HubDbasQuestionnaireModule'
import HubEpworthQuizIntroModule from './view/components/pages/HubEpworthQuizIntroModule'
import HubEpworthQuizResultsModule from './view/components/pages/HubEpworthQuizResultsModule'
import HubEpworthQuestionnaireModule from './view/components/pages/HubEpworthQuestionnaireModule'
import HubModule from './view/components/pages/HubModule'
import OnboardingInterestsModule from './view/components/pages/OnboardingInterestsModule'
import OnboardingWelcomeModule from './view/components/pages/OnboardingWelcomeModule'
import SleepReportModule from './view/components/pages/SleepReportModule'
import UpgradeModule from './view/components/pages/UpgradeModule'
import ResourceModule from './view/components/resources/ResourceModule'
import ResourcesModule from './view/components/resources/ResourcesModule'
import { addQueryParamsToInitialData } from './view/utils/queryParams'
import { getPageDataForRequest } from './view/utils/routing'
import { initialiseSentry } from './view/utils/sentry'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const APP_ELEMENT_ID = `___client`

const ADDITIONAL_STYLES = css`
  html {
    height: 100%;
  }

  body {
    overflow: hidden;
    min-height: 100%;
  }
`

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const loader = async (props) => getPageDataForRequest(props, CONTENT)

// -----------------------------------------------------------------------------
// Router
// -----------------------------------------------------------------------------

const ROUTER = createBrowserRouter([
  {
    element: <RootLayoutModule />,
    errorElement: (
      <RootLayoutModule>
        <ErrorPageModule />
      </RootLayoutModule>
    ),
    children: [
      {
        path: `upgrade`,
        element: <UpgradeModule />,
        loader,
      },
      {
        path: `resources`,
        element: <ResourcesModule />,
        loader,
      },
      {
        path: `resources/:id`,
        element: <ResourceModule />,
        loader,
      },
      {
        path: `sleep-review-report`,
        element: <SleepReportModule />,
        loader,
      },
      {
        path: `hub`,
        element: <HubModule />,
        loader,
      },
      {
        path: `hub/about-therapy`,
        element: <HubAboutTherapyModule />,
        loader,
      },
      {
        path: `hub/access-therapy`,
        element: <HubAccessTherapyModule />,
        loader,
      },
      {
        path: `hub/about-nhs-therapy`,
        element: <HubAboutNhsTherapyModule />,
        loader,
      },
      {
        path: `hub/buy-therapy`,
        element: <HubBuyTherapyModule />,
        loader,
      },
      {
        path: `hub/buy-therapy-grant`,
        element: <HubBuyTherapyGrantModule />,
        loader,
      },
      {
        path: `hub/are-you-getting-enough-sleep/intro`,
        element: <HubEpworthQuizIntroModule />,
        loader,
      },
      {
        path: `hub/are-you-getting-enough-sleep/questions`,
        element: <HubEpworthQuestionnaireModule />,
      },
      {
        path: `hub/are-you-getting-enough-sleep/results`,
        element: <HubEpworthQuizResultsModule />,
        loader,
      },
      {
        path: `hub/what-do-you-know-about-sleep/intro`,
        element: <HubDbasQuizIntroModule />,
        loader,
      },
      {
        path: `hub/what-do-you-know-about-sleep/questions`,
        element: <HubDbasQuestionnaireModule />,
      },
      {
        path: `hub/what-do-you-know-about-sleep/results`,
        element: <HubDbasQuizResultsModule />,
        loader,
      },
      {
        path: `onboarding/welcome`,
        element: <OnboardingWelcomeModule />,
        loader,
      },
      {
        path: `onboarding/nhs-welcome`,
        element: <OnboardingWelcomeModule />,
        loader,
      },
      {
        path: `onboarding/interests`,
        element: <OnboardingInterestsModule />,
        loader,
      },
      {
        path: `feedback/thank-you`,
        element: <FeedbackThankYouModule />,
        loader,
      },
      {
        path: `feedback/end-of-therapy`,
        element: <FeedbackEndOfTherapyModule />,
        loader,
      },
      {
        path: `feedback/end-of-therapy/thank-you`,
        element: <FeedbackEndOfTherapyThankYouModule />,
        loader,
      },
      {
        path: `feedback/written-testimonial`,
        element: <FeedbackWrittenTestimonialModule />,
        loader,
      },
      {
        path: `feedback/video-testimonial`,
        element: <FeedbackVideoTestimonialModule />,
        loader,
      },
    ],
  },
])

// -----------------------------------------------------------------------------
// Render App Component
//
// This is the app entry-point.
// -----------------------------------------------------------------------------

// eslint-disable-next-line import/prefer-default-export
export const initApp = ({ appElementId } = {}) => {
  // Initialise Sentry as early as possible
  // Note that we don't need error reporting in development or under test, only
  // on remote servers.
  const shouldEnableErrorReporter = !isDevelopmentBuild() && !isTestBuild()
  initialiseSentry({ isEnabled: shouldEnableErrorReporter })

  const initialDataWithQueryParams = addQueryParamsToInitialData()

  const domNode = document.getElementById(appElementId)
  const root = createRoot(domNode)

  root.render(
    <React.StrictMode>
      <Providers appElementId={appElementId}>
        <GlobalStyles additionalStyles={ADDITIONAL_STYLES} />
        <ErrorBoundary Fallback={TopLevelErrorNotification}>
          <AppModule initalData={initialDataWithQueryParams}>
            <RouterProvider router={ROUTER} />
          </AppModule>
        </ErrorBoundary>
      </Providers>
    </React.StrictMode>
  )
}

initApp({
  appElementId: APP_ELEMENT_ID,
})
